/**
 * §14.4 — Uniform spatial hash for collision + neighbour queries.
 * Cell size 4 u over a 200×200 arena => 50×50 cells. Rebuilt every tick, zero allocation.
 * Large entities (r > 2 u) insert into every cell their AABB overlaps.
 *
 * The grid is **toroidal** (DECISIONS.md AMD-24). A query window that runs off an edge wraps
 * to the opposite edge instead of clamping — clamping made the seam a wall for every neighbour
 * query. Column/row indices are reduced modulo `cols`/`rows`; a window wider than the grid is
 * capped at the full grid so a cell is never visited twice. Results stay deterministic: the
 * scan order is a pure function of the query arguments.
 */

export const CELL_SIZE = 4;

export interface HashedEntity {
  pos: { x: number; y: number };
  hitbox_radius: number;
  pool_index: number;
}

export class SpatialHash {
  readonly cols: number;
  readonly rows: number;
  readonly cellSize: number;
  readonly originX: number;
  readonly originY: number;
  /** cellStart[c] .. cellStart[c+1] indexes into `items`. */
  private cellCount: Int32Array;
  private cellStart: Int32Array;
  private items: Int32Array;
  private tmpCellIdx: Int32Array;
  private tmpEntity: Int32Array;
  private pairCount = 0;
  queryCount = 0;
  candidateChecks = 0;

  constructor(width: number, height: number, cellSize = CELL_SIZE, maxEntities = 2048) {
    this.cellSize = cellSize;
    this.cols = Math.ceil(width / cellSize);
    this.rows = Math.ceil(height / cellSize);
    this.originX = -width / 2;
    this.originY = -height / 2;
    const cells = this.cols * this.rows;
    this.cellCount = new Int32Array(cells + 1);
    this.cellStart = new Int32Array(cells + 1);
    // worst case: a boss overlapping many cells; allow 8 insertions per entity average
    const cap = maxEntities * 8;
    this.items = new Int32Array(cap);
    this.tmpCellIdx = new Int32Array(cap);
    this.tmpEntity = new Int32Array(cap);
  }

  /** Reduce a column index onto the torus. `cx` may be any integer. */
  private wrapCol(cx: number): number {
    const c = cx % this.cols;
    return c < 0 ? c + this.cols : c;
  }

  /** Reduce a row index onto the torus. */
  private wrapRow(cy: number): number {
    const r = cy % this.rows;
    return r < 0 ? r + this.rows : r;
  }

  private cellOf(x: number, y: number): number {
    const cx = this.wrapCol(Math.floor((x - this.originX) / this.cellSize));
    const cy = this.wrapRow(Math.floor((y - this.originY) / this.cellSize));
    return cy * this.cols + cx;
  }

  /** Begin a rebuild. Call insert() for every live entity, then finish(). */
  begin(): void {
    this.cellCount.fill(0);
    this.pairCount = 0;
    this.queryCount = 0;
    this.candidateChecks = 0;
  }

  insert(poolIndex: number, x: number, y: number, radius: number): void {
    const cap = this.tmpEntity.length;
    if (radius <= this.cellSize / 2) {
      if (this.pairCount >= cap) return;
      const c = this.cellOf(x, y);
      this.tmpCellIdx[this.pairCount] = c;
      this.tmpEntity[this.pairCount] = poolIndex;
      this.pairCount++;
      this.cellCount[c]++;
      return;
    }
    let cx0 = Math.floor((x - radius - this.originX) / this.cellSize);
    let cx1 = Math.floor((x + radius - this.originX) / this.cellSize);
    let cy0 = Math.floor((y - radius - this.originY) / this.cellSize);
    let cy1 = Math.floor((y + radius - this.originY) / this.cellSize);
    if (cx1 - cx0 >= this.cols) {
      cx0 = 0;
      cx1 = this.cols - 1;
    }
    if (cy1 - cy0 >= this.rows) {
      cy0 = 0;
      cy1 = this.rows - 1;
    }
    for (let cy = cy0; cy <= cy1; cy++) {
      const rowBase = this.wrapRow(cy) * this.cols;
      for (let cx = cx0; cx <= cx1; cx++) {
        if (this.pairCount >= cap) return;
        const c = rowBase + this.wrapCol(cx);
        this.tmpCellIdx[this.pairCount] = c;
        this.tmpEntity[this.pairCount] = poolIndex;
        this.pairCount++;
        this.cellCount[c]++;
      }
    }
  }

  finish(): void {
    // prefix sum
    const cells = this.cols * this.rows;
    let acc = 0;
    for (let i = 0; i < cells; i++) {
      this.cellStart[i] = acc;
      acc += this.cellCount[i];
    }
    this.cellStart[cells] = acc;
    // counting sort into items using cellCount as a moving cursor
    const cursor = this.cellCount;
    for (let i = 0; i < cells; i++) cursor[i] = this.cellStart[i];
    for (let i = 0; i < this.pairCount; i++) {
      const c = this.tmpCellIdx[i];
      this.items[cursor[c]++] = this.tmpEntity[i];
    }
  }

  /**
   * Circle query. Writes candidate pool indices into `out`, returns the count.
   * Results are in ascending cell order then insertion order — deterministic.
   * Duplicates are possible for large entities; callers dedupe via a seen-stamp array.
   */
  queryCircle(x: number, y: number, r: number, out: Int32Array, maxOut: number): number {
    this.queryCount++;
    let cx0 = Math.floor((x - r - this.originX) / this.cellSize);
    let cx1 = Math.floor((x + r - this.originX) / this.cellSize);
    let cy0 = Math.floor((y - r - this.originY) / this.cellSize);
    let cy1 = Math.floor((y + r - this.originY) / this.cellSize);
    if (cx1 - cx0 >= this.cols) {
      cx0 = 0;
      cx1 = this.cols - 1;
    }
    if (cy1 - cy0 >= this.rows) {
      cy0 = 0;
      cy1 = this.rows - 1;
    }
    let n = 0;
    for (let cy = cy0; cy <= cy1; cy++) {
      const rowBase = this.wrapRow(cy) * this.cols;
      for (let cx = cx0; cx <= cx1; cx++) {
        const c = rowBase + this.wrapCol(cx);
        const s = this.cellStart[c];
        const e = this.cellStart[c + 1];
        for (let i = s; i < e; i++) {
          if (n >= maxOut) return n;
          out[n++] = this.items[i];
          this.candidateChecks++;
        }
      }
    }
    return n;
  }

  /**
   * Swept-segment query via 2D DDA. Collects candidates along the path so
   * tunnelling is impossible at any speed (§14.4).
   */
  querySegment(x0: number, y0: number, x1: number, y1: number, r: number, out: Int32Array, maxOut: number): number {
    this.queryCount++;
    // Pad by radius: walk the DDA and gather a 1-cell halo when the radius exceeds half a cell.
    const halo = r > this.cellSize * 0.5 ? Math.ceil(r / this.cellSize) : 0;
    let cx = Math.floor((x0 - this.originX) / this.cellSize);
    let cy = Math.floor((y0 - this.originY) / this.cellSize);
    const cxEnd = Math.floor((x1 - this.originX) / this.cellSize);
    const cyEnd = Math.floor((y1 - this.originY) / this.cellSize);
    const dx = x1 - x0;
    const dy = y1 - y0;
    const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
    const invDx = dx !== 0 ? 1 / Math.abs(dx) : Infinity;
    const invDy = dy !== 0 ? 1 / Math.abs(dy) : Infinity;
    const nextBoundaryX = this.originX + (cx + (stepX > 0 ? 1 : 0)) * this.cellSize;
    const nextBoundaryY = this.originY + (cy + (stepY > 0 ? 1 : 0)) * this.cellSize;
    let tMaxX = dx !== 0 ? Math.abs(nextBoundaryX - x0) * invDx : Infinity;
    let tMaxY = dy !== 0 ? Math.abs(nextBoundaryY - y0) * invDy : Infinity;
    const tDeltaX = dx !== 0 ? this.cellSize * invDx : Infinity;
    const tDeltaY = dy !== 0 ? this.cellSize * invDy : Infinity;

    let n = 0;
    let guard = 0;
    const maxCells = this.cols + this.rows + 4;
    for (;;) {
      n = this.gatherCellHalo(cx, cy, halo, out, maxOut, n);
      if (n >= maxOut) return n;
      if ((cx === cxEnd && cy === cyEnd) || ++guard > maxCells) break;
      if (tMaxX < tMaxY) {
        tMaxX += tDeltaX;
        cx += stepX;
      } else {
        tMaxY += tDeltaY;
        cy += stepY;
      }
      // No bounds break: the walk continues across the seam, wrapping cell indices.
    }
    return n;
  }

  private gatherCellHalo(cx: number, cy: number, halo: number, out: Int32Array, maxOut: number, n: number): number {
    const hx = halo * 2 + 1 >= this.cols ? (this.cols - 1) >> 1 : halo;
    const hy = halo * 2 + 1 >= this.rows ? (this.rows - 1) >> 1 : halo;
    const x0 = cx - hx;
    const x1 = cx + hx;
    const y0 = cy - hy;
    const y1 = cy + hy;
    for (let y = y0; y <= y1; y++) {
      const rowBase = this.wrapRow(y) * this.cols;
      for (let x = x0; x <= x1; x++) {
        const c = rowBase + this.wrapCol(x);
        const s = this.cellStart[c];
        const e = this.cellStart[c + 1];
        for (let i = s; i < e; i++) {
          if (n >= maxOut) return n;
          out[n++] = this.items[i];
          this.candidateChecks++;
        }
      }
    }
    return n;
  }
}

/**
 * Swept circle-vs-circle test. Returns time of impact in [0,1] or -1 for no hit.
 * `ax,ay` moving from p0 by (dx,dy) against a static circle at (bx,by).
 */
export function sweptCircle(
  ax: number,
  ay: number,
  dx: number,
  dy: number,
  ar: number,
  bx: number,
  by: number,
  br: number
): number {
  const rx = ax - bx;
  const ry = ay - by;
  const R = ar + br;
  const c = rx * rx + ry * ry - R * R;
  if (c <= 0) return 0; // already overlapping
  const a = dx * dx + dy * dy;
  if (a < 1e-12) return -1;
  const b = rx * dx + ry * dy;
  if (b >= 0) return -1; // moving away
  const disc = b * b - a * c;
  if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / a;
  return t >= 0 && t <= 1 ? t : -1;
}
